import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

// Hook for checking a single permission
export const usePermission = (permissionKey) => {
  const { hasPermission } = usePermissions();
  return hasPermission(permissionKey);
};

// Hook for checking multiple permissions (AND logic)
export const usePermissions_All = (permissionKeys) => {
  const { hasAllPermissions } = usePermissions();
  return hasAllPermissions(permissionKeys);
};

// Hook for checking multiple permissions (OR logic)
export const usePermissions_Any = (permissionKeys) => {
  const { hasAnyPermission } = usePermissions();
  return hasAnyPermission(permissionKeys);
};

export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [roleConfig, setRoleConfig] = useState(null);

  // Fetch user permissions when authenticated
  const fetchPermissions = useCallback(async () => {
    if (!user?.id) {
      setPermissions({});
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/permissions/user/${user.id}`);
      setPermissions(response.data.permissions || {});
      setRoleConfig({
        role: response.data.role,
        user_id: response.data.user_id
      });
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // Fall back to empty permissions on error
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchPermissions();
    } else {
      setPermissions({});
      setRoleConfig(null);
      setLoading(false);
    }
  }, [isAuthenticated, user?.id, fetchPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permissionKey) => {
    // Admin always has all permissions (fallback)
    if (user?.role === 'admin') return true;
    return permissions[permissionKey] === true;
  }, [permissions, user?.role]);

  // Check if user has ALL of the specified permissions
  const hasAllPermissions = useCallback((permissionKeys) => {
    if (!Array.isArray(permissionKeys)) return false;
    if (user?.role === 'admin') return true;
    return permissionKeys.every(key => permissions[key] === true);
  }, [permissions, user?.role]);

  // Check if user has ANY of the specified permissions
  const hasAnyPermission = useCallback((permissionKeys) => {
    if (!Array.isArray(permissionKeys)) return false;
    if (user?.role === 'admin') return true;
    return permissionKeys.some(key => permissions[key] === true);
  }, [permissions, user?.role]);

  // Refresh permissions (useful after role changes)
  const refreshPermissions = useCallback(() => {
    setLoading(true);
    fetchPermissions();
  }, [fetchPermissions]);

  const value = {
    permissions,
    loading,
    roleConfig,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    refreshPermissions,
    // Expose raw check for edge cases
    checkPermission: (key) => permissions[key] === true
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

// HOC for protecting components based on permissions
export const withPermission = (WrappedComponent, requiredPermission, FallbackComponent = null) => {
  return function PermissionProtectedComponent(props) {
    const { hasPermission, loading } = usePermissions();
    
    if (loading) {
      return null; // Or a loading spinner
    }
    
    if (!hasPermission(requiredPermission)) {
      return FallbackComponent ? <FallbackComponent {...props} /> : null;
    }
    
    return <WrappedComponent {...props} />;
  };
};

// Component wrapper for conditional rendering based on permissions
export const PermissionGate = ({ 
  permission, 
  permissions: permissionsList,
  requireAll = false,
  children, 
  fallback = null 
}) => {
  const { hasPermission, hasAllPermissions, hasAnyPermission, loading } = usePermissions();
  
  if (loading) {
    return null;
  }
  
  let hasAccess = false;
  
  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissionsList && Array.isArray(permissionsList)) {
    hasAccess = requireAll 
      ? hasAllPermissions(permissionsList) 
      : hasAnyPermission(permissionsList);
  }
  
  return hasAccess ? children : fallback;
};

export default PermissionsContext;
